import {UserWayApiClient} from '../../../userway/sdk/UserWayApiClient';
import {AxiosHeaders} from 'axios';
import {inject, Injectable} from '@angular/core';
import {AutoUnsubscribe} from '../../decorators/auto-unsubscribe.decorator';
import {CUSTOM_SWITCH_TEAM_HEADER, SUBSCRIPTIONS} from '../../common/utils/constants';
import {AuthQuery} from '../../common/elf-store/auth/auth.query';
import {environment} from '../../../environments/environment';
import {from, map, Observable} from 'rxjs';
import {SSOApiClient} from '../../../userway/sdk/SSOApiClient';
import {SSO_URLS_LIST} from '../../common/utils/sso-url-list';
import {LocalStorage} from '../../decorators/local-storage.decorator';
import {TokenErrorHandlerService} from '../token-error-handler.service';
import {EventEmitterService} from '../event-emitter.service';

@Injectable({
  providedIn: 'root',
})
@AutoUnsubscribe()
export class ApiClient {
  @LocalStorage('') uWTargetSwitchId: string;

  private token: string;
  private client: UserWayApiClient<any>;
  private ssoApiClient: SSOApiClient<any>;
  private authQuery = inject(AuthQuery);
  eventEmitterService = inject(EventEmitterService);
  tokenErrorHandlerService = inject(TokenErrorHandlerService);

  constructor() {
    this.createApiClient();

    this.applyInterceptorsToClient(this.client);
    this.applyInterceptorsToClient(this.ssoApiClient);
  }

  initAxiosHeadersSubscriptions(): void {
    this[SUBSCRIPTIONS].add(
      this.eventEmitterService.updateHeaders.subscribe(() => {
        this.client.instance.defaults.headers.common = this.getAuthHeaders();
        this.ssoApiClient.instance.defaults.headers.common = this.getAuthHeaders();
      }),
    );

    this[SUBSCRIPTIONS].add(
      this.authQuery.token$.subscribe(token => {
        this.token = token;
        this.client.instance.defaults.headers.common = {
          ...this.client.instance.defaults.headers.common,
          ...this.getAuthHeaders(),
        };
        this.ssoApiClient.instance.defaults.headers.common = {
          ...this.ssoApiClient.instance.defaults.headers.common,
          ...this.getAuthHeaders(),
        };
      }),
    );
  }

  createApiClient(): void {
    this.client = new UserWayApiClient({
      baseURL: environment.apiUserWay,
      headers: {
        ...this.getAuthHeaders(),
      },
    });
    this.ssoApiClient = new SSOApiClient({
      baseURL: environment.apiUserWay,
      headers: {
        ...this.getAuthHeaders(),
      },
    });
  }

  get api() {
    return this.client;
  }

  get ssoApi() {
    return this.ssoApiClient;
  }

  fetch<T>(request: Promise<{data: T}>): Observable<T> {
    return from(request).pipe(map(response => response.data));
  }

  createAxiosCustomHeaderContext(additionalHeaders: Record<string, string> = {}) {
    return new AxiosHeaders().set({...additionalHeaders});
  }

  clearHeaders() {
    this.client.instance.defaults.headers.common = {};
    this.ssoApiClient.instance.defaults.headers.common = {};
  }

  private getAuthHeaders() {
    const unitId = localStorage.getItem('uWTargetSwitchId');

    if (!this.token) return {};

    let token = new AxiosHeaders().set('x-auth-token', this.token);
    token.set('authorization', this.token);

    if (unitId) {
      token.set(CUSTOM_SWITCH_TEAM_HEADER, JSON.parse(unitId));
    } else {
      token.delete(CUSTOM_SWITCH_TEAM_HEADER);
    }

    return token;
  }

  private applyInterceptorsToClient(client: UserWayApiClient<any> | SSOApiClient<any>) {
    client.instance.interceptors.request.use(config => {
      if (this.shouldExcludeSSOHeaders(config.url)) {
        config.headers['x-sso-request-origin'] = 'WIDGET_ORIGIN';
      }

      const SUB_ACCOUNT_UNIT_ID = config.headers[CUSTOM_SWITCH_TEAM_HEADER];

      if (!!this.token && !!this.uWTargetSwitchId && config.headers['authorization'] !== 'none') {
        config.headers[CUSTOM_SWITCH_TEAM_HEADER] = SUB_ACCOUNT_UNIT_ID || this.uWTargetSwitchId;
      }

      return config;
    });

    client.instance.interceptors.response.use(
      response => response,
      error => {
        this.tokenErrorHandlerService.processHttpError(error);
        return Promise.reject(error);
      },
    );
  }

  private shouldExcludeSSOHeaders(url: string): boolean {
    return SSO_URLS_LIST.some(ssoUrl => url.includes(ssoUrl));
  }
}
